<template>
  <section class="room-log-page">
    <div class="hero">
      <div>
        <h2>Room Mapping Client Logs</h2>
      </div>
      <div class="hero-stats">
        <span>Total Records</span>
        <strong>{{ pagination.total }}</strong>
      </div>
    </div>

    <room-mapping-listing-actions class="main-listing-actions" />

    <div class="filter-card">
      <div class="filter-grid">
        <input v-model="filters.date" type="date" class="filter-input" />
        <input v-model.trim="filters.requestid" type="text" class="filter-input" placeholder="Request ID" />
        <input v-model.trim="filters.ssid" type="text" class="filter-input" placeholder="SSID" />
        <input v-model.trim="filters.group_id" type="text" class="filter-input" placeholder="Group ID" />
        <input v-model.trim="filters.client" type="text" class="filter-input" placeholder="Client Name" />
        <b-form-select
          v-model="filters.mapping_status"
          :options="mappingStatusOptions"
          class="filter-input"
          aria-label="Mapping Status"
        />
      </div>
      <div class="filter-actions">
        <b-button variant="primary" @click="applyFilters" :disabled="loading">Apply Filters</b-button>
        <b-button variant="outline-secondary" @click="resetFilters" :disabled="loading">Reset</b-button>
      </div>
    </div>

    <div class="table-card">
      <div class="table-meta">
        <span>Page {{ pagination.page }} / {{ pagination.total_pages || 1 }}</span>
        <span>Per Page</span>
        <b-form-select v-model="pagination.per_page" :options="pageSizeOptions" class="per-page" @change="changePageSize" />
      </div>

      <div v-if="loading" class="loading-box">
        <div class="table-skeleton">
          <div class="skeleton-row" v-for="index in 6" :key="index">
            <span class="skeleton-cell large"></span>
            <span class="skeleton-cell medium"></span>
            <span class="skeleton-cell medium"></span>
            <span class="skeleton-cell small"></span>
          </div>
        </div>
      </div>

      <div v-else-if="logs.length === 0" class="empty-box">
        No logs found for selected filters.
      </div>

      <div v-else class="table-wrap">
        <table class="log-table">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                class="sortable-header"
                :class="{ active: sortBy === column.key, disabled: column.sortable === false }"
                @click="column.sortable === false ? null : handleSort(column.key)"
              >
                <span>{{ column.label }}</span>
                <span class="sort-indicator">
                  <span v-if="column.sortable === false">•</span>
                  <span v-else-if="sortBy === column.key">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in logs" :key="getRowKey(row)">
              <td>{{ formatClientCell(row) }}</td>
              <td>{{ formatCell(row.city) }}</td>
              <td>{{ formatCell(row.create_date) }}</td>
              <td>{{ formatCell(row.group_id) }}</td>
              <td>
                <span class="status-badge" :class="getStatusClass(row.status)">{{ row.status }}</span>
              </td>
              <td>{{ formatTimeTaken(row.time_taken) }}</td>
              <td>{{ formatCell(row.row_count) }}</td>
              <td>
                <span class="mapping-count-badge" :class="getMappingClass(row)">
                  {{ getMappingCount(row).mapped }}/{{ getMappingCount(row).total }}
                </span>
              </td>
              <td>{{ formatCell(row.remark) }}</td>
              <td>
                <button
                  type="button"
                  class="others-trigger"
                  v-b-tooltip.hover
                  title="View Others summary"
                  @click="openOthersPage(row)"
                >
                  <b-icon icon="stars" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pager">
        <b-button variant="outline-primary" @click="goPrev" :disabled="pagination.page <= 1 || loading">Prev</b-button>
        <div class="pager-mid">
          <button
            v-for="page in visiblePages"
            :key="page"
            class="page-pill"
            :class="{ active: page === pagination.page }"
            @click="goToPage(page)"
            :disabled="loading"
          >
            {{ page }}
          </button>
        </div>
        <b-button
          variant="outline-primary"
          @click="goNext"
          :disabled="pagination.page >= pagination.total_pages || loading"
        >
          Next
        </b-button>
      </div>
    </div>
<b-modal
  v-model="showOthersModal"
  centered
  size="xl"
  hide-footer
  modal-class="others-modal-shell"
  content-class="others-modal-content"
  body-class="others-modal-body"
  @hidden="resetOthersModal"
>
  <div v-if="othersModalLoading" class="others-skeleton">
    <div class="others-skeleton-card"></div>
    <div class="others-skeleton-row" v-for="index in 4" :key="`skeleton-${index}`"></div>
  </div>

  <div v-else class="others-modal-layout">
    <div class="others-header-panel">
      <div class="others-modal-header">
        <div class="others-title-block">
          <h4>Room Summary</h4>
          <p>
            {{ activeOtherLog ? `${formatCell(activeOtherLog.client)} • ${formatCell(activeOtherLog.city)}` : 'Detailed category summary' }}
          </p>
        </div>
        <div class="others-header-actions">
          <button
            type="button"
            class="manage-mapping-btn"
            title="Manage Mapping Data"
            aria-label="Manage Mapping Data"
            @click="showMappingManager = true"
          >
            <b-icon icon="sliders" />
            <span>Manage Mapping Data</span>
          </button>
          <button type="button" class="modal-close-btn" @click="showOthersModal = false">✕</button>
        </div>
      </div>
    </div>

    <div class="others-summary-strip">
      <div class="summary-chip">
        <span>Request ID</span>
        <strong>{{ activeOtherLog ? formatCell(activeOtherLog.rid) : '-' }}</strong>
      </div>
      <div class="summary-chip">
        <span>Session ID</span>
        <strong>{{ activeOtherLog ? formatCell(activeOtherLog.ssid) : '-' }}</strong>
      </div>
      <div class="summary-chip">
        <span>Group ID</span>
        <strong>{{ activeOtherLog ? formatCell(activeOtherLog.group_id) : '-' }}</strong>
      </div>
      <div class="summary-chip">
        <span>Created At</span>
        <strong>{{ activeOtherLog ? formatCell(activeOtherLog.create_date) : '-' }}</strong>
      </div>
      <div class="summary-chip">
        <span>Total Rooms</span>
        <strong>{{ activeOtherLog ? formatCell(activeOtherLog.row_count) : '-' }}</strong>
      </div>
    </div>

    <div v-if="hasOthersData" class="others-collapse-card">
      <button type="button" class="others-collapse-strip" @click="showOthersSummary = !showOthersSummary">
        <div class="others-collapse-title">
          <b-icon icon="collection" />
          <span>Others</span>
        </div>
        <div class="others-collapse-meta">
          <span>Room Summary Others</span>
          <strong>{{ roomSummaryOthersDisplay }}</strong>
          <b-icon :icon="showOthersSummary ? 'chevron-up' : 'chevron-down'" />
        </div>
      </button>

      <div v-if="showOthersSummary" class="others-table-wrap">
        <div class="others-table-topbar">
          <div>
            <h5>Other type count summary</h5>
            <p class="others-summary-note">`Others Count` = saved existing count, `Invalid Others Count` = data that should not have gone into Others.</p>
          </div>
          <span class="others-mini-badge">{{ otherRows.length }} Items</span>
        </div>

        <div class="others-table-scroll">
          <table class="others-table">
            <thead>
              <tr>
                <th>Other Type</th>
                <th>Others Count</th>
                <th>Invalid Others Count</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in otherRows" :key="item.key" :class="{ active: selectedOtherKey === item.key }">
                <td>
                  <div class="other-type-cell">
                    <span class="other-type-dot"></span>
                    <span>{{ item.label }}</span>
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    class="count-view-btn count-view-btn-other"
                    :class="{ active: isSelectedOtherDetail(item, 'others') }"
                    :disabled="item.originalCount <= 0 || othersDetailsLoading"
                    v-b-tooltip.hover
                    :title="item.originalCount > 0 ? `View ${item.label} Others Count` : 'No Others Count data available'"
                    @click="toggleOtherDetails(item, 'others')"
                  >
                    <b-icon :icon="isSelectedOtherDetail(item, 'others') ? 'eye-fill' : 'eye'" />
                    <span>{{ item.originalDisplayCount }}</span>
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    class="count-view-btn count-view-btn-invalid"
                    :class="{ active: isSelectedOtherDetail(item, 'invalid') }"
                    :disabled="item.count <= 0 || othersDetailsLoading"
                    v-b-tooltip.hover
                    :title="item.count > 0 ? `View ${item.label} Invalid Others Count` : 'No Invalid Others Count data available'"
                    @click="toggleOtherDetails(item, 'invalid')"
                  >
                    <b-icon :icon="isSelectedOtherDetail(item, 'invalid') ? 'eye-fill' : 'eye'" />
                    <span>{{ item.displayCount }}</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="othersDetailsLoading" class="others-detail-loading">
      <div class="others-detail-skeleton"></div>
      <div class="others-detail-skeleton"></div>
      <div class="others-detail-skeleton"></div>
    </div>

    <div v-else-if="selectedOtherKey && othersDetailRows.length" class="others-detail-wrap">
      <div class="others-detail-topbar">
        <div>
          <h5>{{ selectedOtherSummary.label }} {{ selectedOtherCountTypeLabel }} Data</h5>
          <p>{{ othersDetailRows.length }} rows available for review.</p>
        </div>
      </div>

      <div class="others-detail-scroll">
        <table class="others-detail-table">
          <thead>
            <tr>
              <th v-for="column in othersDetailColumns" :key="column">{{ formatDetailColumn(column) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(detailRow, index) in othersDetailRows" :key="`${selectedOtherKey}-${index}`">
              <td v-for="column in othersDetailColumns" :key="`${selectedOtherKey}-${index}-${column}`">
                {{ formatCell(detailRow[column]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="selectedOtherKey" class="others-empty-state">
      <b-icon icon="inbox" class="empty-icon" />
      <h5>No data found</h5>
      <p>{{ selectedOtherSummary.label }} {{ selectedOtherCountTypeLabel }} has no rows for this query.</p>
    </div>

  </div>
</b-modal>
    <room-mapping-data-manager v-model="showMappingManager" />
  </section>
</template>

<script>
import axios from '@axios'
import { BButton, BFormSelect, BIcon, BModal, VBTooltip } from 'bootstrap-vue'
import RoomMappingDataManager from './RoomMappingDataManager.vue'
import RoomMappingListingActions from './RoomMappingListingActions.vue'
import { setRoomMappingOthersContext } from './roomMappingOthersContext'

export default {
  components: { BButton, BFormSelect, BIcon, BModal, RoomMappingDataManager, RoomMappingListingActions },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      logs: [],
      loading: false,
      showOthersModal: false,
      showMappingManager: false,
      showOthersSummary: false,
      othersModalLoading: false,
      activeOtherLog: null,
      selectedOtherKey: '',
      selectedOtherCountType: '',
      othersModalTimer: null,
      othersSummaryCounts: {
        bed_type_others: 0,
        meals_others: 0,
        view_others: 0,
        room_summary_others: 0,
      },
      othersDetailsLoading: false,
      othersDetailColumns: [],
      othersDetailRows: [],
      filters: {
        date: '',
        requestid: '',
        ssid: '',
        group_id: '',
        client: '',
        mapping_status: '',
      },
      pagination: {
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1,
      },
      pageSizeOptions: [10, 20, 50, 100],
      mappingStatusOptions: [
        { value: '', text: 'Mapping Status' },
        { value: 'mapped', text: 'Mapped' },
        { value: 'unmapped', text: 'Unmapped' },
      ],
      sortBy: 'create_date',
      sortOrder: 'desc',
      columns: [
        { key: 'client', label: 'Client Name' },
        { key: 'city', label: 'City' },
        { key: 'create_date', label: 'Created At' },
        { key: 'group_id', label: 'Group ID' },
        { key: 'status', label: 'Status' },
        { key: 'time_taken', label: 'Time Taken' },
        { key: 'total', label: 'Total' },
        { key: 'mapping_count', label: 'Mapping Count' },
        { key: 'remark', label: 'Remark' },
        { key: 'others', label: 'Others', sortable: false },
      ],
    }
  },
  computed: {
    visiblePages() {
      const current = this.pagination.page
      const total = this.pagination.total_pages || 1
      const start = Math.max(1, current - 2)
      const end = Math.min(total, current + 2)
      const pages = []
      for (let i = start; i <= end; i += 1) {
        pages.push(i)
      }
      return pages
    },
    otherRows() {
      const current = this.activeOtherLog || {}
      return [
        {
          key: 'room_summary_others',
          label: 'Room Summary Others',
          count: this.toCount(this.othersSummaryCounts.room_summary_others),
          displayCount: this.formatOtherCount(this.othersSummaryCounts.room_summary_others),
          originalCount: this.toCount(current.room_summary_others),
          originalDisplayCount: this.formatOtherCount(current.room_summary_others),
        },
        {
          key: 'bed_type_others',
          label: 'Bed Type Others',
          count: this.toCount(this.othersSummaryCounts.bed_type_others),
          displayCount: this.formatOtherCount(this.othersSummaryCounts.bed_type_others),
          originalCount: this.toCount(current.bed_type_others),
          originalDisplayCount: this.formatOtherCount(current.bed_type_others),
        },
        {
          key: 'meals_others',
          label: 'Meals Others',
          count: this.toCount(this.othersSummaryCounts.meals_others),
          displayCount: this.formatOtherCount(this.othersSummaryCounts.meals_others),
          originalCount: this.toCount(current.meals_others),
          originalDisplayCount: this.formatOtherCount(current.meals_others),
        },
        {
          key: 'view_others',
          label: 'View Others',
          count: this.toCount(this.othersSummaryCounts.view_others),
          displayCount: this.formatOtherCount(this.othersSummaryCounts.view_others),
          originalCount: this.toCount(current.view_others),
          originalDisplayCount: this.formatOtherCount(current.view_others),
        },
      ]
    },
    selectedOtherSummary() {
      return this.otherRows.find(item => item.key === this.selectedOtherKey) || this.otherRows[0] || {
        label: '-',
        displayCount: '-',
      }
    },
    selectedOtherCountTypeLabel() {
      return this.selectedOtherCountType === 'others' ? 'Others Count' : 'Invalid Others Count'
    },
    hasOthersData() {
      return this.otherRows.length > 0
    },
    roomSummaryOthersDisplay() {
      return this.formatOtherCount(this.activeOtherLog && this.activeOtherLog.room_summary_others)
    },
  },
  mounted() {
    this.restoreRouteState()
    this.fetchLogs()
  },
  beforeDestroy() {
    if (this.othersModalTimer) {
      clearTimeout(this.othersModalTimer)
      this.othersModalTimer = null
    }
  },
  methods: {
    openOthersPage(row) {
      setRoomMappingOthersContext(row)
      const returnTo = this.$router.resolve({
        name: 'roomMappingClientLogs',
        query: this.getRouteState(),
      }).href
      this.$router.push({
        name: 'roomMappingOthersSummary',
        query: {
          ssid: row.ssid,
          group_id: row.group_id,
          return_to: returnTo,
        },
      })
    },
    getRouteState() {
      return {
        page: String(this.pagination.page),
        per_page: String(this.pagination.per_page),
        date: this.filters.date,
        requestid: this.filters.requestid,
        ssid: this.filters.ssid,
        group_id: this.filters.group_id,
        client: this.filters.client,
        mapping_status: this.filters.mapping_status,
        sort_by: this.sortBy,
        sort_order: this.sortOrder,
      }
    },
    restoreRouteState() {
      const query = this.$route.query || {}
      const page = Number.parseInt(query.page, 10)
      const perPage = Number.parseInt(query.per_page, 10)
      this.pagination.page = Number.isInteger(page) && page > 0 ? page : 1
      this.pagination.per_page = this.pageSizeOptions.includes(perPage) ? perPage : 20
      this.filters = {
        date: String(query.date || ''),
        requestid: String(query.requestid || ''),
        ssid: String(query.ssid || ''),
        group_id: String(query.group_id || ''),
        client: String(query.client || ''),
        mapping_status: ['mapped', 'unmapped'].includes(query.mapping_status) ? query.mapping_status : '',
      }
      const sortableColumns = this.columns.filter(column => column.sortable !== false).map(column => column.key)
      this.sortBy = sortableColumns.includes(query.sort_by) ? query.sort_by : 'create_date'
      this.sortOrder = query.sort_order === 'asc' ? 'asc' : 'desc'
    },
    syncRouteState() {
      const target = {
        name: 'roomMappingClientLogs',
        query: this.getRouteState(),
      }
      this.$router.replace(target).catch(error => {
        if (!error || error.name !== 'NavigationDuplicated') throw error
      })
    },
    async fetchLogs() {
      this.syncRouteState()
      this.loading = true
      try {
        const payload = {
          page: this.pagination.page,
          per_page: this.pagination.per_page,
          date: this.filters.date,
          requestid: this.filters.requestid,
          ssid: this.filters.ssid,
          group_id: this.filters.group_id,
          client: this.filters.client,
          mapping_status: this.filters.mapping_status,
          sort_by: this.sortBy,
          sort_order: this.sortOrder,
        }
        const { data } = await axios.post('room_mapping_client_logs', payload)
        this.logs = data.logs || []
        this.pagination = {
          ...this.pagination,
          ...(data.pagination || {}),
        }
        this.sortBy = (data.sorting && data.sorting.sort_by) || this.sortBy
        this.sortOrder = (data.sorting && data.sorting.sort_order) || this.sortOrder
      } catch (error) {
        this.logs = []
        this.pagination.total = 0
        this.pagination.total_pages = 1
      } finally {
        this.loading = false
      }
    },
    applyFilters() {
      this.pagination.page = 1
      this.fetchLogs()
    },
    resetFilters() {
      this.filters = {
        date: '',
        requestid: '',
        ssid: '',
        group_id: '',
        client: '',
        mapping_status: '',
      }
      this.pagination.page = 1
      this.fetchLogs()
    },
    changePageSize() {
      this.pagination.page = 1
      this.fetchLogs()
    },
    goPrev() {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
        this.fetchLogs()
      }
    },
    goNext() {
      if (this.pagination.page < this.pagination.total_pages) {
        this.pagination.page += 1
        this.fetchLogs()
      }
    },
    goToPage(page) {
      if (page !== this.pagination.page) {
        this.pagination.page = page
        this.fetchLogs()
      }
    },
    handleSort(columnKey) {
      if (this.sortBy === columnKey) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = columnKey
        this.sortOrder = columnKey === 'client' || columnKey === 'city' || columnKey === 'group_id' || columnKey === 'remark' ? 'asc' : 'desc'
      }
      this.pagination.page = 1
      this.fetchLogs()
    },
    async loadOthersSummary(row) {
      this.othersModalLoading = true
      this.showOthersSummary = false
      this.selectedOtherKey = ''
      this.selectedOtherCountType = ''
      this.othersDetailsLoading = false
      this.othersDetailColumns = []
      this.othersDetailRows = []
      this.othersSummaryCounts = {
        bed_type_others: 0,
        meals_others: 0,
        view_others: 0,
        room_summary_others: 0,
      }
      this.activeOtherLog = row
      try {
        const { data } = await axios.post('room_mapping_client_log_others', {
          ssid: row.ssid,
          group_id: row.group_id,
        })
        this.othersSummaryCounts = {
          ...this.othersSummaryCounts,
          ...(data.summary || {}),
        }
      } catch (error) {
        this.othersSummaryCounts = {
          bed_type_others: 0,
          meals_others: 0,
          view_others: 0,
          room_summary_others: 0,
        }
      } finally {
        this.othersModalLoading = false
      }
    },
    async openOthersModal(row) {
      this.showOthersModal = true
      if (this.othersModalTimer) {
        clearTimeout(this.othersModalTimer)
      }
      await this.loadOthersSummary(row)
    },
    resetOthersModal() {
      if (this.othersModalTimer) {
        clearTimeout(this.othersModalTimer)
        this.othersModalTimer = null
      }
      this.othersModalLoading = false
      this.othersDetailsLoading = false
      this.activeOtherLog = null
      this.showOthersSummary = false
      this.selectedOtherKey = ''
      this.selectedOtherCountType = ''
      this.othersDetailColumns = []
      this.othersDetailRows = []
      this.othersSummaryCounts = {
        bed_type_others: 0,
        meals_others: 0,
        view_others: 0,
        room_summary_others: 0,
      }
    },
    async toggleOtherDetails(item, countType = 'invalid') {
      const safeCountType = countType === 'others' ? 'others' : 'invalid'
      const availableCount = safeCountType === 'others' ? item && item.originalCount : item && item.count
      if (!item || availableCount <= 0 || !this.activeOtherLog) return

      if (this.isSelectedOtherDetail(item, safeCountType) && this.othersDetailRows.length) {
        this.selectedOtherKey = ''
        this.selectedOtherCountType = ''
        this.othersDetailColumns = []
        this.othersDetailRows = []
        return
      }

      this.selectedOtherKey = item.key
      this.selectedOtherCountType = safeCountType
      this.othersDetailsLoading = true
      this.othersDetailColumns = []
      this.othersDetailRows = []

      try {
        const { data } = await axios.post('room_mapping_client_log_others', {
          ssid: this.activeOtherLog.ssid,
          group_id: this.activeOtherLog.group_id,
          detail_type: item.key,
          count_type: safeCountType,
        })
        const detail = data.detail || {}
        this.othersSummaryCounts = {
          ...this.othersSummaryCounts,
          ...(data.summary || {}),
        }
        this.othersDetailColumns = detail.columns || []
        this.othersDetailRows = detail.rows || []
      } catch (error) {
        this.othersDetailColumns = []
        this.othersDetailRows = []
      } finally {
        this.othersDetailsLoading = false
      }
    },
    isSelectedOtherDetail(item, countType) {
      return Boolean(item && this.selectedOtherKey === item.key && this.selectedOtherCountType === countType)
    },
    getStatusClass(status) {
      if ((status || '').startsWith('Success')) return 'success'
      if ((status || '').startsWith('Partial Failed')) return 'warning'
      if ((status || '').startsWith('Failed')) return 'danger'
      return 'neutral'
    },
    formatTimeTaken(value) {
      if (value === null || value === undefined || value === '') return '-'
      return `${value} sec`
    },
    formatCell(value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    formatClientCell(row) {
      const client = this.formatCell(row && row.client)
      if (client === '-') return '-'
      return row && row.client_type ? `${client} (${row.client_type})` : client
    },
    formatOtherCount(value) {
      return value === null || value === undefined || value === '' ? '0' : `${value}`
    },
    toCount(value) {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    getRowKey(row) {
      return `${(row && (row.rid || row.group_id)) || 'row'}-${(row && row.updated_at) || ''}`
    },
    getMappingCount(row) {
      const total = this.toCount(row && row.row_count)
      const mappedFromApi = this.toCount(row && row.mapping_completed_count)
      const roomSummaryOthers = this.toCount(row && row.room_summary_others)
      const mapped = mappedFromApi || Math.max(total - roomSummaryOthers, 0)
      return {
        mapped: Math.min(mapped, total || mapped),
        total,
      }
    },
    getMappingClass(row) {
      const count = this.getMappingCount(row)
      if (!count.total) return 'danger'
      const ratio = count.mapped / count.total
      if (ratio >= 0.95) return 'success'
      if (ratio > 0) return 'warning'
      return 'danger'
    },
    formatDetailColumn(value) {
      return this.formatCell(value).replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
    },
  },
}
</script>
<style scoped>
.room-log-page {
  padding: 20px;
  background: linear-gradient(180deg, #f3f7fb 0%, #f9fbfd 100%);
  min-height: calc(100vh - 40px);
}
.hero {
  border-radius: 18px;
  padding: 18px 22px;
  margin-bottom: 14px;
  color: #11213a;
  background: linear-gradient(120deg, #dff3ff 0%, #f6faff 60%, #f0f8ff 100%);
  border: 1px solid #cae9ff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.hero h2 {
  margin: 0;
  font-weight: 700;
}
.hero p {
  margin: 4px 0 0;
  color: #3f5678;
}
.hero-stats {
  background: #fff;
  padding: 8px 14px;
  border-radius: 12px;
  border: 1px solid #dfebf7;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.hero-stats span {
  font-size: 12px;
  color: #6f86a3;
}
.hero-stats strong {
  font-size: 20px;
}
.main-listing-actions {
  margin: 0 0 14px;
}
.filter-card,
.table-card {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e4edf6;
  box-shadow: 0 10px 30px rgba(36, 79, 124, 0.07);
}
.filter-card {
  margin-bottom: 14px;
}
.filter-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}
.filter-input {
  height: 40px;
  border: 1px solid #d2e1f0;
  border-radius: 10px;
  padding: 0 12px;
}
.filter-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
.table-meta {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: #4b607d;
}
.per-page {
  width: 100px;
}
.table-wrap {
  overflow: auto;
}
.log-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.log-table thead th {
  background: #123f72;
  color: #fff;
  font-size: 13px;
  padding: 10px;
  text-align: left;
  position: sticky;
  top: 0;
}
.sortable-header {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.sortable-header.disabled {
  cursor: default;
}
.sortable-header span:first-child {
  margin-right: 6px;
}
.sortable-header.active {
  background: #0e325b;
}
.sort-indicator {
  font-size: 11px;
  opacity: 0.95;
}
.log-table tbody td {
  padding: 10px;
  border-bottom: 1px solid #e8eef5;
  font-size: 13px;
  vertical-align: middle;
}
.log-table tbody tr:nth-child(even) {
  background: #f8fbff;
}
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.status-badge.success {
  background: #dff7ea;
  color: #117a44;
}
.status-badge.warning {
  background: #fff3d6;
  color: #9a6700;
}
.status-badge.danger {
  background: #fde6e7;
  color: #c23a3a;
}
.status-badge.neutral {
  background: #eef3f8;
  color: #4f6280;
}
.mapping-count-badge {
  min-width: 76px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid transparent;
}
.mapping-count-badge.success {
  background: #dff7ea;
  border-color: #b8ebcf;
  color: #0f7a43;
}
.mapping-count-badge.warning {
  background: #fff3d6;
  border-color: #f7d98e;
  color: #9a6700;
}
.mapping-count-badge.danger {
  background: #fde6e7;
  border-color: #f2bdc1;
  color: #c23a3a;
}
.others-trigger {
  width: 38px;
  height: 38px;
  border: 1px solid rgba(99, 127, 255, 0.18);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(70, 126, 255, 0.14), rgba(157, 108, 255, 0.16));
  color: #3453c7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 24px rgba(75, 102, 191, 0.14);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.others-trigger:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(75, 102, 191, 0.2);
  background: linear-gradient(135deg, rgba(70, 126, 255, 0.2), rgba(157, 108, 255, 0.24));
}
.loading-box,
.empty-box {
  min-height: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #4f6280;
}
.table-skeleton,
.others-skeleton {
  width: 100%;
}
.skeleton-row,
.others-skeleton-row,
.others-skeleton-card {
  position: relative;
  overflow: hidden;
  background: #eef4fb;
}
.skeleton-row {
  display: grid;
  grid-template-columns: 2fr 1.1fr 1.1fr 0.8fr;
  gap: 12px;
  margin-bottom: 12px;
}
.skeleton-cell,
.others-skeleton-row,
.others-skeleton-card {
  display: block;
  border-radius: 12px;
  min-height: 18px;
}
.skeleton-cell {
  height: 18px;
}
.skeleton-cell.large {
  width: 100%;
}
.skeleton-cell.medium {
  width: 100%;
}
.skeleton-cell.small {
  width: 100%;
}
.skeleton-row::after,
.others-skeleton-row::after,
.others-skeleton-card::after,
.skeleton-cell::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent);
  animation: shimmer 1.3s infinite;
}
.others-skeleton-card {
  height: 96px;
  margin-bottom: 18px;
  border-radius: 24px;
}
.others-skeleton-row {
  height: 58px;
  margin-bottom: 14px;
  border-radius: 18px;
}

/* ===== OTHERS MODAL — THEME-MATCHED FIX ===== */
.others-header-panel {
  padding: 0 0 16px;
  border-bottom: 1px solid #e4edf6;
}
.others-modal-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.others-title-block {
  min-width: 0;
}
.others-modal-header h4 {
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  color: #11213a;
}
.others-modal-header p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #5f738f;
  overflow-wrap: anywhere;
}
.modal-close-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #d6e2ef;
  flex-shrink: 0;
  border-radius: 8px;
  background: #fff;
  color: #4b607d;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.modal-close-btn:hover {
  background: #f5f9fd;
  border-color: #c5d6ea;
}
.others-header-actions {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-shrink: 0;
}
.manage-mapping-btn {
  min-height: 36px;
  padding: 0 13px;
  border: 1px solid #6d28d9;
  border-radius: 8px;
  background: #6d28d9;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  box-shadow: 0 7px 16px rgba(109, 40, 217, 0.2);
}
.manage-mapping-btn:hover {
  border-color: #5b21b6;
  background: #5b21b6;
}
.others-modal-shell >>> .modal-dialog {
  max-width: 860px;
}
.others-modal-shell >>> .modal-header {
  display: none;
}
.others-modal-shell >>> .modal-content {
  overflow: hidden;
}
.others-modal-content {
  border: 1px solid #dce8f4;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(17, 33, 58, 0.16);
  overflow: hidden;
}
.others-modal-body {
  padding: 20px;
  background: #ffffff;
  max-height: 76vh;
  overflow-y: auto;
}
.others-modal-layout {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.others-summary-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.summary-chip {
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #dce8f4;
}
.summary-chip span {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6f86a3;
  margin-bottom: 6px;
}
.summary-chip strong {
  display: block;
  color: #11213a;
  font-size: 14px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}
.others-collapse-card {
  border: 1px solid #dce8f4;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 10px 26px rgba(36, 79, 124, 0.07);
}
.others-collapse-strip {
  width: 100%;
  min-height: 56px;
  border: 0;
  background: linear-gradient(135deg, #f7fbff 0%, #ffffff 54%, #eef6ff 100%);
  color: #11213a;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid #e4edf6;
}
.others-collapse-title,
.others-collapse-meta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.others-collapse-title {
  font-size: 15px;
  font-weight: 800;
}
.others-collapse-title .b-icon {
  color: #1673d5;
  font-size: 18px;
}
.others-collapse-meta {
  flex-wrap: wrap;
  justify-content: flex-end;
  color: #5f738f;
  font-size: 12px;
  font-weight: 700;
}
.others-collapse-meta strong {
  min-width: 42px;
  min-height: 30px;
  padding: 5px 12px;
  border-radius: 999px;
  background: #123f72;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(18, 63, 114, 0.22);
}
.others-table-wrap {
  background: #ffffff;
  border: 1px solid #dce8f4;
  border-radius: 10px;
  overflow: hidden;
}
.others-table-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e8eef5;
}
.others-table-topbar h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #11213a;
}
.others-summary-note {
  margin: 4px 0 0;
  font-size: 12px;
  color: #5f738f;
}
.others-mini-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #eef3f8;
  color: #123f72;
  font-size: 12px;
  font-weight: 700;
}
.others-table-scroll {
  overflow-x: auto;
}
.others-table {
  width: 100%;
  min-width: 620px;
  border-collapse: separate;
  border-spacing: 0;
}
.others-table thead th {
  background: #123f72;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 10px 14px;
  text-align: left;
}
.others-table tbody tr {
  background: #ffffff;
  transition: background 0.15s ease;
}
.others-table tbody tr:nth-child(even) {
  background: #f8fbff;
}
.others-table tbody tr.active {
  background: #eaf2ff;
}
.others-table tbody tr:hover {
  background: #eef5ff;
}
.others-table tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #e8eef5;
  font-size: 13px;
  vertical-align: middle;
}
.other-type-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: #1e2948;
}
.other-type-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #1673d5;
}
.count-view-btn {
  min-width: 78px;
  min-height: 32px;
  border: 1px solid #cdddf0;
  border-radius: 999px;
  padding: 6px 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #ffffff;
  color: #123f72;
  font-weight: 800;
  font-size: 12px;
  box-shadow: 0 8px 18px rgba(36, 79, 124, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}
.count-view-btn .b-icon {
  font-size: 13px;
}
.count-view-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(36, 79, 124, 0.14);
}
.count-view-btn-other {
  background: #f8fbff;
  border-color: #d8e6f5;
  color: #315473;
}
.count-view-btn-other:hover:not(:disabled),
.count-view-btn-other.active {
  background: #123f72;
  border-color: #123f72;
  color: #ffffff;
}
.count-view-btn-invalid {
  background: #fff8f0;
  border-color: #f5d9b4;
  color: #925a12;
}
.count-view-btn-invalid:hover:not(:disabled),
.count-view-btn-invalid.active {
  background: #1673d5;
  border-color: #1673d5;
  color: #fff;
}
.count-view-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}
.others-detail-loading,
.others-detail-wrap {
  border: 1px solid #dce8f4;
  border-radius: 10px;
  background: #fff;
}
.others-detail-loading {
  padding: 14px;
}
.others-detail-skeleton {
  height: 42px;
  border-radius: 8px;
  background: #eef4fb;
  margin-bottom: 10px;
}
.others-detail-skeleton:last-child {
  margin-bottom: 0;
}
.others-detail-topbar {
  padding: 14px;
  border-bottom: 1px solid #e8eef5;
}
.others-detail-topbar h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #11213a;
}
.others-detail-topbar p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #5f738f;
}
.others-detail-scroll {
  overflow: auto;
}
.others-detail-table {
  width: 100%;
  min-width: 720px;
  border-collapse: separate;
  border-spacing: 0;
}
.others-detail-table thead th {
  background: #f5f9fd;
  color: #123f72;
  font-size: 12px;
  font-weight: 700;
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #dce8f4;
  white-space: nowrap;
}
.others-detail-table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #e8eef5;
  font-size: 13px;
  color: #11213a;
  vertical-align: top;
  word-break: break-word;
}
.others-detail-table tbody tr:nth-child(even) {
  background: #fbfdff;
}
.others-empty-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  border-radius: 12px;
  background: #f8fbff;
  border: 1px solid #dce8f4;
}
.empty-icon {
  font-size: 36px;
  color: #1673d5;
  margin-bottom: 12px;
}
.others-empty-state h5 {
  margin: 0 0 6px;
  color: #11213a;
}
.others-empty-state p {
  margin: 0;
  color: #4b607d;
}
/* ===== END OTHERS MODAL FIX ===== */

.pager {
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.pager-mid {
  display: flex;
  gap: 6px;
}
.page-pill {
  border: 1px solid #ccdcf0;
  background: #fff;
  color: #2f4968;
  border-radius: 8px;
  min-width: 34px;
  height: 34px;
}
.page-pill.active {
  background: #1673d5;
  color: #fff;
  border-color: #1673d5;
}
@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
@media (max-width: 768px) {
  .room-log-page {
    padding: 12px;
  }
  .hero {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
  .others-modal-body {
    padding: 16px;
    max-height: 82vh;
  }
  .others-header-panel {
    padding: 0 0 14px;
  }
  .others-modal-header h4 {
    font-size: 22px;
  }
  .others-modal-header {
    align-items: flex-start;
  }
  .others-header-actions {
    align-items: flex-end;
    flex-direction: column-reverse;
  }
  .manage-mapping-btn span {
    display: none;
  }
  .manage-mapping-btn {
    width: 36px;
    padding: 0;
  }
  .others-table-topbar {
    align-items: flex-start;
    flex-direction: column;
  }
  .others-detail-table {
    min-width: 640px;
  }
}
</style>
